<template>
    <vvo v-slot="v" tag="div" class="sc-edit">
        <div class="sc-edit-inner">
            <!-- 고정 액션 바 -->
            <header class="sc-actionbar">
                <button
                    type="button"
                    class="sc-btn sc-btn--ghost sc-btn--icon"
                    :aria-label="$t('닫기')"
                    :title="$t('닫기 (Esc)')"
                    @click="requestCancel"
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                    >
                        <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
                    </svg>
                </button>
                <span class="sc-ab-vr"></span>
                <div>
                    <div class="sc-ab-title">
                        {{ title }}
                        <span class="sc-dirty" :class="{ 'is-dirty': isDirty }">{{ stateLabel }}</span>
                    </div>
                    <div v-if="summaryText" class="sc-ab-summary" :title="summaryText">{{ summaryText }}</div>
                </div>
                <span class="sc-grow"></span>
                <div v-if="detail" class="sc-checks" tabindex="-1" @focusout="onChecksFocusOut">
                    <button
                        type="button"
                        class="sc-checks-btn"
                        :class="{ 'has-warn': warnings.length }"
                        :aria-expanded="String(showChecks)"
                        @click="showChecks = !showChecks"
                    >
                        <template v-if="warnings.length">! {{ $t("확인 필요") }} {{ warnings.length }}</template>
                        <template v-else>✓ {{ $t("확인 완료") }}</template>
                    </button>
                    <div v-if="showChecks" class="sc-checks-pop" role="dialog" :aria-label="$t('저장 전 확인')">
                        <div class="sc-checks-title">{{ $t("저장 전 확인") }}</div>
                        <button
                            v-for="(check, i) in checks"
                            :key="i"
                            type="button"
                            class="sc-check-item"
                            @click="goSection(check.section)"
                        >
                            <span class="mark" :class="check.level">{{ check.level === "ok" ? "✓" : "!" }}</span>
                            <span>{{ check.text }}</span>
                        </button>
                    </div>
                </div>
                <button type="button" class="sc-btn" :disabled="saving" @click="requestCancel">
                    {{ $t("취소") }}
                </button>
                <button
                    type="button"
                    class="sc-btn sc-btn--primary"
                    :disabled="!detail || saving"
                    @click="onValidate(v, save)"
                >
                    {{ saving ? $t("저장 중...") : $t("저장") }}
                </button>
            </header>

            <div v-if="!detail" class="sc-empty">
                <span>{{ $t("불러오는 중...") }}</span>
            </div>

            <div v-else class="sc-edit-body">
                <div class="sc-form-scroll">
                    <div ref="sec1"><EditBasicSection :detail="detail" :warn="sectionWarn[1]" /></div>
                    <div ref="sec2"><EditTargetSection :detail="detail" :warn="sectionWarn[2]" /></div>
                    <div ref="sec3"><EditEventSection :detail="detail" :warn="sectionWarn[3]" /></div>
                    <div ref="sec4"><EditExceptionSection :detail="detail" /></div>
                </div>
            </div>
        </div>
    </vvo>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import EditBasicSection from "./edit/EditBasicSection.vue";
import EditTargetSection from "./edit/EditTargetSection.vue";
import EditEventSection from "./edit/EditEventSection.vue";
import EditExceptionSection from "./edit/EditExceptionSection.vue";
import {
    fetchScheduleDetail,
    fillTargetNames,
    newScheduleTemplate,
    fetchDefaultHolidays,
    ApiError,
} from "../utils/scheduleApi";
import { buildChecks } from "../utils/scheduleChecks";
import { summarizeDays, hhmm, equipTypeName } from "../utils/scheduleFormat";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: { EditBasicSection, EditTargetSection, EditEventSection, EditExceptionSection },
    props: {
        /** null 이면 신규 작성 */
        schdCtrlIdx: { type: [Number, String], default: null },
    },
    data() {
        return {
            detail: null,
            snapshot: "",
            saving: false,
            showChecks: false,
        };
    },
    computed: {
        isNew() {
            return this.schdCtrlIdx === null || this.schdCtrlIdx === undefined;
        },
        title() {
            const name = this.detail && this.detail.schdCtrlName;
            return name || (this.isNew ? this.$t("제어 스케줄 추가") : "-");
        },
        isDirty() {
            return !!this.detail && JSON.stringify(this.detail) !== this.snapshot;
        },
        stateLabel() {
            if (this.isDirty) return this.$t("편집 중 · 저장 안 됨");
            return this.isNew ? this.$t("신규 작성") : this.$t("편집 중");
        },
        checks() {
            return this.detail ? buildChecks(this.detail, (k) => this.$t(k)) : [];
        },
        warnings() {
            return this.checks.filter((c) => c.level === "warn");
        },
        sectionWarn() {
            const warn = {};
            this.warnings.forEach((c) => (warn[c.section] = true));
            return warn;
        },
        /** 상단 바 한 줄 요약: "매주 월–금 06:40 · 12:00 / 냉난방기 그룹 2 · 설비 3" */
        summaryText() {
            const d = this.detail;
            if (!d) return "";
            const days = summarizeDays(d.repeatConfig);
            const times = d.events
                .map((e) => hhmm(e.schdHour))
                .filter(Boolean)
                .sort()
                .join(" · ");
            const when = [days ? `${this.$t("매주")} ${days}` : "", times].filter(Boolean).join(" ");
            const targets = d.targets
                .map((t) => {
                    const groups = d.groups.filter((g) => g.equipType == t.equipType).length;
                    const equips = d.equips.filter((e) => e.equipType == t.equipType).length;
                    const parts = [];
                    if (groups) parts.push(`${this.$t("그룹")} ${groups}`);
                    if (equips) parts.push(`${this.$t("설비")} ${equips}`);
                    return parts.length ? `${equipTypeName(this.$store, t.equipType)} ${parts.join(" · ")}` : "";
                })
                .filter(Boolean)
                .join(", ");
            return [when, targets].filter(Boolean).join(" / ");
        },
    },
    async created() {
        await this.init();
        window.addEventListener("beforeunload", this.onBeforeUnload);
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    },
    methods: {
        async init() {
            try {
                let detail;
                if (this.isNew) {
                    detail = newScheduleTemplate();
                    detail.exceptConfig = await fetchDefaultHolidays();
                } else {
                    detail = await fetchScheduleDetail(this.schdCtrlIdx);
                    await fillTargetNames(detail);
                }
                this.detail = detail;
                this.snapshot = JSON.stringify(detail);
            } catch (e) {
                console.error(e);
                if (e instanceof ApiError) this.alertWarning(e.message, "", e.detail);
                else this.alertNoti(popupMessages.COMMON_NO_SEARCH_DATA_POPUP_MESSAGE);
                this.$emit("cancel");
            }
        },
        onBeforeUnload(e) {
            if (!this.isDirty) return;
            e.preventDefault();
            e.returnValue = "";
        },
        /** 미저장 변경이 있으면 확인 후 이탈 가능 여부를 돌려준다 (부모/라우터 가드에서도 사용) */
        async canLeave() {
            if (!this.isDirty) return true;
            const result = await this.alertConfirmWarning(popupMessages.COMMON_EDIT_CANCEL_POPUP_MESSAGE);
            return !!result.value;
        },
        async requestCancel() {
            if (this.saving) return;
            if (await this.canLeave()) this.$emit("cancel");
        },
        async save() {
            this.saving = true;
            try {
                const payload = JSON.parse(JSON.stringify(this.detail));
                // 체크된 요일만 "Y" 로 전송
                Object.keys(payload.repeatConfig).forEach((key) => {
                    if (!payload.repeatConfig[key]) delete payload.repeatConfig[key];
                    else payload.repeatConfig[key] = "Y";
                });

                const result = await backEndApi.equipControl.saveEquipCtrlSch(payload);
                if (result.status == 200) {
                    await this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.snapshot = JSON.stringify(this.detail);
                    const savedIdx = this.detail.schdCtrlIdx ?? (result.data && result.data.schdCtrlIdx) ?? null;
                    this.$emit("saved", savedIdx);
                } else {
                    this.alertWarning(
                        `${result.data.message}`,
                        "",
                        `${typeof result.data.detail == "string" ? result.data.detail : ""}`,
                    );
                }
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_SAVE_ERROR_POPUP_MESSAGE);
                console.error(e);
            } finally {
                this.saving = false;
            }
        },
        /** 확인 항목을 누르면 해당 섹션으로 이동 */
        goSection(n) {
            const el = this.$refs[`sec${n}`];
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            this.showChecks = false;
        },
        onChecksFocusOut(e) {
            if (!e.currentTarget.contains(e.relatedTarget)) this.showChecks = false;
        },
    },
};
</script>
